import { supabase } from './supabase';
import { gcsUploadFile } from './gcsStorage';

/**
 * Site Compliance Service
 *
 * CRUD operations for site/premises compliance requirements:
 * - Fire safety, electrical, water systems, structural inspections
 * - LOLER (lifting equipment), PSSR (pressure systems)
 * - Asbestos, legionella, environmental compliance
 * - Insurance and permits
 */
export const siteComplianceService = {
  // Load all site compliance data for a project
  async loadProjectSiteCompliance(projectId, { approvedOnly = false } = {}) {
    try {
      // Load site requirements
      let query = supabase
        .from('site_compliance_requirements')
        .select('*')
        .eq('project_id', projectId);

      if (approvedOnly) {
        query = query.eq('approval_status', 'approved');
      }

      const { data: requirements, error: reqError } = await query
        .order('display_order', { ascending: true })
        .order('created_date', { ascending: true });

      if (reqError) throw reqError;

      // Load wizard data from unified compliance_wizard_data table
      const { data: wizardDataArray, error: wizError } = await supabase
        .from('compliance_wizard_data')
        .select('*')
        .eq('project_id', projectId);

      // Handle the case where no data exists
      const wizardData = wizardDataArray && wizardDataArray.length > 0 ? wizardDataArray[0] : null;

      if (wizError && wizError.code !== 'PGRST116') {
        console.error('Error loading site wizard data:', wizError);
      }

      return {
        success: true,
        requirements: requirements || [],
        wizardData: wizardData || null
      };
    } catch (error) {
      console.error('Error loading site compliance data:', error);
      return { success: false, error: error.message };
    }
  },

  // Save or update site wizard data
  async saveSiteWizardData(projectId, wizardData) {
    try {
      // Build update object with only provided fields that have actual values
      const dataToSave = {
        project_id: projectId,
        updated_date: new Date().toISOString()
      };

      // Add fields if they exist and are not null/undefined
      const source = wizardData;

      // Site info
      if (source.site_type !== undefined && source.site_type !== null) {
        dataToSave.site_type = source.site_type;
      }
      if (source.building_age !== undefined && source.building_age !== null) {
        dataToSave.building_age = source.building_age;
      }
      if (source.building_ownership !== undefined && source.building_ownership !== null) {
        dataToSave.building_ownership = source.building_ownership;
      }
      if (source.floor_count !== undefined && source.floor_count !== null) {
        dataToSave.floor_count = source.floor_count;
      }
      if (source.total_area_sqft !== undefined && source.total_area_sqft !== null) {
        dataToSave.total_area_sqft = source.total_area_sqft;
      }

      // Systems
      if (source.fire_systems !== undefined && source.fire_systems !== null) {
        dataToSave.fire_systems = source.fire_systems;
      }
      if (source.electrical_systems !== undefined && source.electrical_systems !== null) {
        dataToSave.electrical_systems = source.electrical_systems;
      }
      if (source.hvac_systems !== undefined && source.hvac_systems !== null) {
        dataToSave.hvac_systems = source.hvac_systems;
      }
      if (source.water_systems !== undefined && source.water_systems !== null) {
        dataToSave.water_systems = source.water_systems;
      }
      if (source.gas_systems !== undefined && source.gas_systems !== null) {
        dataToSave.gas_systems = source.gas_systems;
      }

      // Hazards
      if (source.asbestos_status !== undefined && source.asbestos_status !== null) {
        dataToSave.asbestos_status = source.asbestos_status;
      }
      if (source.legionella_risk !== undefined && source.legionella_risk !== null) {
        dataToSave.legionella_risk = source.legionella_risk;
      }
      if (source.hazardous_storage !== undefined && source.hazardous_storage !== null) {
        dataToSave.hazardous_storage = source.hazardous_storage;
      }

      // Equipment
      if (source.fixed_equipment !== undefined && source.fixed_equipment !== null) {
        dataToSave.fixed_equipment = source.fixed_equipment;
      }
      if (source.lifting_equipment !== undefined && source.lifting_equipment !== null) {
        dataToSave.lifting_equipment = source.lifting_equipment;
      }
      if (source.pressure_systems !== undefined && source.pressure_systems !== null) {
        dataToSave.pressure_systems = source.pressure_systems;
      }

      // Access
      if (source.public_access !== undefined && source.public_access !== null) {
        dataToSave.public_access = source.public_access;
      }
      if (source.disabled_access !== undefined && source.disabled_access !== null) {
        dataToSave.disabled_access = source.disabled_access;
      }

      // Only update if we have fields to update beyond project_id and updated_date
      if (Object.keys(dataToSave).length <= 2) {
        console.log('No fields to update, skipping save');
        return { success: true, data: null };
      }

      // Use unified compliance_wizard_data table
      const { data, error } = await supabase
        .from('compliance_wizard_data')
        .upsert(dataToSave, {
          onConflict: 'project_id'
        })
        .select()
        .single();

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('Error saving site wizard data:', error);
      return { success: false, error: error.message };
    }
  },

  // Add a single site requirement
  async addSiteRequirement(projectId, requirement) {
    try {
      const { data: userData } = await supabase.auth.getUser();
      const userId = userData?.user?.id;

      // Get user profile
      const { data: userProfile } = await supabase
        .from('user_profiles')
        .select('id')
        .eq('auth_id', userId)
        .single();

      const { data, error } = await supabase
        .from('site_compliance_requirements')
        .insert({
          project_id: projectId,
          ...requirement,
          created_by: userProfile?.id,
          created_date: new Date().toISOString(),
          updated_date: new Date().toISOString()
        })
        .select()
        .single();

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('Error adding site requirement:', error);
      return { success: false, error: error.message };
    }
  },

  // Add multiple site requirements
  async addMultipleSiteRequirements(projectId, requirements) {
    try {
      const { data: userData } = await supabase.auth.getUser();
      const userId = userData?.user?.id;

      // Get user profile
      const { data: userProfile } = await supabase
        .from('user_profiles')
        .select('id')
        .eq('auth_id', userId)
        .single();

      const requirementsWithMeta = requirements.map((req, index) => ({
        project_id: projectId,
        ...req,
        created_by: userProfile?.id,
        display_order: index,
        created_date: new Date().toISOString(),
        updated_date: new Date().toISOString()
      }));

      const { data, error } = await supabase
        .from('site_compliance_requirements')
        .insert(requirementsWithMeta)
        .select();

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('Error adding multiple site requirements:', error);
      return { success: false, error: error.message };
    }
  },

  // Update a site requirement
  async updateSiteRequirement(requirementId, updates) {
    try {
      const { data, error } = await supabase
        .from('site_compliance_requirements')
        .update({
          ...updates,
          updated_date: new Date().toISOString()
        })
        .eq('id', requirementId)
        .select()
        .single();

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('Error updating site requirement:', error);
      return { success: false, error: error.message };
    }
  },

  // Delete a site requirement
  async deleteSiteRequirement(requirementId) {
    try {
      const { error } = await supabase
        .from('site_compliance_requirements')
        .delete()
        .eq('id', requirementId);

      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error('Error deleting site requirement:', error);
      return { success: false, error: error.message };
    }
  },

  // Delete all site requirements for a project
  async deleteAllSiteRequirements(projectId) {
    try {
      const { error } = await supabase
        .from('site_compliance_requirements')
        .delete()
        .eq('project_id', projectId);

      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error('Error deleting all site requirements:', error);
      return { success: false, error: error.message };
    }
  },

  // Update compliance status (mark compliant/non-compliant/expired)
  async updateComplianceStatus(requirementId, status, complianceNotes = null, evidenceUrl = null) {
    try {
      const updates = {
        status,
        updated_date: new Date().toISOString()
      };

      if (complianceNotes !== null) {
        updates.compliance_notes = complianceNotes;
      }
      if (evidenceUrl !== null) {
        updates.evidence_url = evidenceUrl;
      }

      // If marking as compliant, set last_completed_date
      if (status === 'compliant') {
        updates.last_completed_date = new Date().toISOString();
      }

      const { data, error } = await supabase
        .from('site_compliance_requirements')
        .update(updates)
        .eq('id', requirementId)
        .select()
        .single();

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('Error updating compliance status:', error);
      return { success: false, error: error.message };
    }
  },

  // Get requirements due within X days
  async getUpcomingDueDates(projectId, days = 30) {
    try {
      const futureDate = new Date();
      futureDate.setDate(futureDate.getDate() + days);

      const { data, error } = await supabase
        .from('site_compliance_requirements')
        .select('*')
        .eq('project_id', projectId)
        .lte('next_due_date', futureDate.toISOString())
        .gte('next_due_date', new Date().toISOString())
        .order('next_due_date', { ascending: true });

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('Error getting upcoming due dates:', error);
      return { success: false, error: error.message };
    }
  },

  // Get overdue requirements
  async getOverdueRequirements(projectId) {
    try {
      const { data, error } = await supabase
        .from('site_compliance_requirements')
        .select('*')
        .eq('project_id', projectId)
        .lt('next_due_date', new Date().toISOString())
        .neq('status', 'compliant')
        .order('next_due_date', { ascending: true });

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('Error getting overdue requirements:', error);
      return { success: false, error: error.message };
    }
  },

  // Get requirements by category
  async getRequirementsByCategory(projectId, category) {
    try {
      const { data, error } = await supabase
        .from('site_compliance_requirements')
        .select('*')
        .eq('project_id', projectId)
        .eq('category', category)
        .order('display_order', { ascending: true });

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('Error getting requirements by category:', error);
      return { success: false, error: error.message };
    }
  },

  // Upload evidence file
  async uploadEvidence(requirementId, file) {
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${requirementId}_${Date.now()}.${fileExt}`;

      const { publicUrl } = await gcsUploadFile(file, {
        folderPath: `uploads/site-compliance-evidence`,
        fileName: fileName,
        contentType: file.type
      });

      // Update requirement with evidence URL
      const { data, error } = await supabase
        .from('site_compliance_requirements')
        .update({
          evidence_url: publicUrl,
          updated_date: new Date().toISOString()
        })
        .eq('id', requirementId)
        .select()
        .single();

      if (error) throw error;
      return { success: true, data, url: publicUrl };
    } catch (error) {
      console.error('Error uploading evidence:', error);
      return { success: false, error: error.message };
    }
  }
};

export default siteComplianceService;
